'use client';

import type { ComponentProps, InputHTMLAttributes } from 'react';

import { Slider as SliderPrimitive } from './shadcn/slider';

/** A scalar app wrapper over Radix Slider's controlled value array. `onCommit` reports the value once a
 *  drag ends or a key press lands (Radix `onValueCommit`), for a caller that saves the value rather than
 *  previewing every step; it is host-only and not part of the plugin kit's `SliderProps`. */
export function Slider({ value, onChange, onCommit, min = 0, max = 100, step = 1, className = '', ...rest }: {
  value: number;
  onChange: (value: number) => void;
  onCommit?: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'min' | 'max' | 'step' | 'type'>) {
  const {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-valuetext': ariaValueText,
    ...rootProps
  } = rest;
  const radixProps = rootProps as unknown as Omit<
    ComponentProps<typeof SliderPrimitive>,
    'className' | 'value' | 'onValueChange' | 'min' | 'max' | 'step' | 'thumbProps'
  >;

  return (
    <SliderPrimitive
      {...radixProps}
      value={[value]}
      min={min}
      max={max}
      step={step}
      thumbProps={{
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy,
        'aria-describedby': ariaDescribedBy,
        'aria-valuetext': ariaValueText,
      }}
      onValueChange={([next]) => {
        if (next !== undefined) onChange(next);
      }}
      {...(onCommit ? { onValueCommit: ([next]: number[]) => { if (next !== undefined) onCommit(next); } } : {})}
      className={className}
    />
  );
}
